import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit2, UserPlus, X, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/field`;

// Pencil beside the WO number → move the WHOLE sheet (everyone's hours + consumables for the week) to another WO.
export function SheetWoEditor({ wo, dateFrom, dateTo, onMoved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const search = async (q) => {
    setValue(q);
    if (!q || q.length < 2) { setResults([]); return; }
    try { const r = await axios.get(`${API}/work-orders`, { params: { q } }); setResults((r.data || []).slice(0, 6)); } catch { setResults([]); }
  };
  const save = async () => {
    const nw = value.trim();
    if (!nw) { toast.error('Enter the new work order number'); return; }
    if (nw === wo.work_order_number) { setOpen(false); return; }
    const people = wo.employees.length;
    if (!window.confirm(`Move this whole sheet — ${people} ${people === 1 ? 'person' : 'people'}, ${wo.total_hours}h and anything logged to it this week — from ${wo.work_order_number} to ${nw}?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/timesheet/reassign-wo-all`, { old_wo: wo.work_order_number, new_wo: nw, date_from: dateFrom, date_to: dateTo });
      toast.success(`Moved ${wo.work_order_number} → ${nw} (${r.data.modified} record${r.data.modified === 1 ? '' : 's'})`);
      setOpen(false); setValue(''); setResults([]);
      onMoved(nw);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not move the sheet'); }
    setBusy(false);
  };
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} title="Wrong work order? Move everyone on this sheet to another WO" data-testid={`ts-sheet-wo-edit-${wo.work_order_number}`}
        className="ml-1 text-slate-500 hover:text-blue-600 align-middle"><Edit2 className="w-3.5 h-3.5 inline" /></button>
    );
  }
  return (
    <span className="relative inline-flex items-center gap-1 ml-2 align-middle" data-testid="ts-sheet-wo-editor">
      <span className="text-xs text-slate-600">→</span>
      <input autoFocus value={value} onChange={(e) => search(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setOpen(false); }}
        placeholder="new WO #" data-testid="ts-sheet-wo-input" className="w-28 px-2 py-1 border border-blue-400 rounded text-xs font-mono text-slate-900 outline-none" />
      <button onClick={save} disabled={busy} data-testid="ts-sheet-wo-save" className="px-2 py-1 rounded bg-blue-600 text-white text-xs font-bold inline-flex items-center gap-1 disabled:opacity-60"><Check className="w-3 h-3" /> Move sheet</button>
      <button onClick={() => { setOpen(false); setResults([]); }} className="p-1 text-slate-500 hover:text-slate-800" data-testid="ts-sheet-wo-cancel"><X className="w-3.5 h-3.5" /></button>
      {results.length > 0 && (
        <div className="absolute z-30 top-full left-0 mt-1 bg-white border border-slate-300 rounded shadow-lg w-64 max-h-48 overflow-auto">
          {results.map((p) => (
            <button key={p.project_number} data-testid={`ts-sheet-wo-opt-${p.project_number}`} onClick={() => { setValue(p.project_number); setResults([]); }}
              className="block w-full text-left px-2 py-1 text-xs text-slate-700 hover:bg-blue-50">
              <span className="font-mono font-bold text-blue-600">{p.project_number}</span> {p.client_name || p.title || ''}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

// "+ Add person" under a sheet: pick staff / a contractor name, type hours per day → rows created via update-timesheet-hours.
export function AddPersonRow({ wo, dayLabels, onAdded }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [people, setPeople] = useState({ staff: [], contractors: [] });
  const [picked, setPicked] = useState(null); // { id, name }
  const [hours, setHours] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try { const r = await axios.get(`${API}/timesheet/people`, { params: { q } }); setPeople(r.data); } catch { setPeople({ staff: [], contractors: [] }); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  const reset = () => { setOpen(false); setQ(''); setPicked(null); setHours({}); };
  const save = async () => {
    const name = picked?.name || q.trim();
    if (!name) { toast.error('Pick or type a name'); return; }
    const days = dayLabels.filter((d) => parseFloat(hours[d.date]) > 0);
    if (!days.length) { toast.error('Enter hours on at least one day'); return; }
    if (wo.employees.some((e) => e.employee_name.toLowerCase() === name.toLowerCase())) { toast.error(`${name} is already on this sheet — edit their cells instead`); return; }
    setBusy(true);
    try {
      for (const d of days) {
        await axios.post(`${API}/update-timesheet-hours`, { employee_name: name, date: d.date, hours: parseFloat(hours[d.date]), work_order_number: wo.work_order_number });
      }
      toast.success(`Added ${name} to ${wo.work_order_number} (${days.length} day${days.length === 1 ? '' : 's'})`);
      reset();
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add'); }
    setBusy(false);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={`ts-add-person-${wo.work_order_number}`}
        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-semibold border border-blue-200">
        <UserPlus className="w-3.5 h-3.5" /> Add person
      </button>
    );
  }
  const matches = [...people.staff, ...people.contractors].filter((p) => !wo.employees.some((e) => e.employee_name.toLowerCase() === p.name.toLowerCase())).slice(0, 8);
  const typedIsNew = q.trim() && !matches.some((m) => m.name.toLowerCase() === q.trim().toLowerCase());
  return (
    <div className="px-3 py-2 bg-blue-50 border-t border-slate-300 flex flex-wrap items-end gap-2" data-testid="ts-add-person-form">
      <div className="relative">
        <label className="block text-[11px] text-slate-500">Who</label>
        {picked ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white border border-blue-300 text-xs font-semibold text-slate-900" data-testid="ts-add-person-picked">{picked.name}
            <button onClick={() => { setPicked(null); setQ(''); }} className="text-slate-500 hover:text-red-600"><X className="w-3 h-3" /></button></span>
        ) : (
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name…" data-testid="ts-add-person-search"
            className="w-48 px-2 py-1 border border-slate-300 rounded text-xs text-slate-900 outline-none focus:border-blue-500" />
        )}
        {!picked && q.length >= 1 && (matches.length > 0 || typedIsNew) && (
          <div className="absolute z-30 top-full left-0 mt-1 bg-white border border-slate-300 rounded shadow-lg w-64 max-h-52 overflow-auto">
            {matches.map((p) => (
              <button key={p.name} onClick={() => setPicked(p)} data-testid={`ts-add-person-opt-${p.name.replace(/\s+/g, '-').toLowerCase()}`}
                className="block w-full text-left px-2 py-1 text-xs text-slate-700 hover:bg-blue-50">{p.name}{p.role ? <span className="text-slate-500"> · {p.role.replace(/_/g, ' ')}</span> : null}</button>
            ))}
            {typedIsNew && (
              <button onClick={() => setPicked({ id: null, name: /\(contractor\)/i.test(q) ? q.trim() : `${q.trim()} (Contractor)` })} data-testid="ts-add-person-new-contractor"
                className="block w-full text-left px-2 py-1 text-xs text-amber-800 bg-amber-50 hover:bg-amber-100">Add "{q.trim()}" as a contractor</button>
            )}
          </div>
        )}
      </div>
      {dayLabels.map((d) => (
        <div key={d.date}>
          <label className="block text-[11px] text-slate-500 text-center">{d.label}</label>
          <input type="number" step="0.5" min="0" value={hours[d.date] || ''} onChange={(e) => setHours((h) => ({ ...h, [d.date]: e.target.value }))} placeholder="—"
            data-testid={`ts-add-person-hours-${d.date}`} className="w-12 px-1 py-1 border border-slate-300 rounded text-xs text-center text-slate-900 outline-none focus:border-blue-500" />
        </div>
      ))}
      <button onClick={save} disabled={busy} data-testid="ts-add-person-save" className="px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-bold disabled:opacity-60">{busy ? 'Adding…' : 'Add'}</button>
      <button onClick={reset} className="px-2 py-1.5 text-xs text-slate-500 hover:text-slate-800" data-testid="ts-add-person-cancel">Cancel</button>
    </div>
  );
}
